import { connect } from 'react-redux'
import { useDispatch } from 'react-redux'
import { useEffect, useState } from 'react'
import { getCookies } from 'cookies-next'
import { Array, Boolean, Record, Static } from 'runtypes'

import Content from '../../../../components/Content/Content'
import NewsApi from '../../../../api/news'
import { CommentsApi } from '../../../../api/comments'
import Comments from '../../../../components/Content/OpenedNewsPageComponents/Comments/Comments'
import CategoryApi from '../../../../api/categories'
import { initializeStore } from '../../../../store'
import {
  Category,
  CategoryApiResponse,
  CommentsPagination,
  Post as PostType,
  PostFromServer,
} from '../../../../types'
import Post from '../../../../components/Content/OpenedNewsPageComponents/Post'
import RelatedPostsHorizontalScroller from '../../../../components/Content/RelatedPostsHorizontalScroller/RelatedPostsHorizontalScroller'
import RightSidePanel from '../../../../components/Content/RightSidePanel/RightSidePanel'
import BannerApi from '../../../../api/banners'
import authenticationApi from '../../../../api/authentication'
import SearchResults from '../../../../components/Content/SearchResults/SearchResults'
import { SearchResponse } from '../../../../types/posts'
import FullWidthAdBanner from '../../../../components/Advertisement/FullWidthAdBanner/FullWidthAdBanner'
import SideVerticalAdvertisementBar from '../../../../components/Advertisement/SideVerticalAdvertisementBar/SideVerticalAdvertisementBar'
import SideAdBanner from '../../../../components/Advertisement/SideAdBanner/SideAdBanner'
import { BannerPosition } from '../../../../types/banner'
import * as Sentry from '@sentry/nextjs'
import EmptySearchResults from '../../../../components/Content/EmptySearchResults/EmptySearchResults'

export const PostPageProps = Record({
  post: PostType,
  commentsPagination: CommentsPagination,
  relatedPostsPerTags: Array(
    PostFromServer.And(
      Record({
        categories: Array(CategoryApiResponse),
      })
    )
  ),
  newestFromCategory: Array(
    PostFromServer.And(
      Record({
        categories: Array(CategoryApiResponse),
      })
    )
  ),
  searchResultsPagination: SearchResponse,
  currentCategory: Category,
  emptySearchResults: Boolean,
})

export type PostPageProps = Static<typeof PostPageProps>

const PostPage = ({
  post,
  relatedPostsPerTags,
  newestFromCategory,
  searchResultsPagination,
  currentCategory,
  emptySearchResults,
}: PostPageProps): JSX.Element => {
  const [categoryPostsPage, setCategoryPostsPage] = useState(1)
  const [posts, setPosts] = useState([])
  const [screenWidth, setScreenWidth] = useState(0)

  useEffect(() => {
    setScreenWidth(window.innerWidth)
  }, [])

  const fetchMorePosts = async (page: number, categoryId: number) => {
    const response = await NewsApi.getNewsByCategoryId(categoryId, page, 2)
    setPosts([...posts, ...response.data.data])
    setCategoryPostsPage(categoryPostsPage + 1)
  }

  useEffect(() => {
    setPosts([post])
    fetchMorePosts(categoryPostsPage, currentCategory.id)
  }, [post])

  useEffect(() => {
    localStorage.setItem('page', 'post')
  }, [])

  const dispatch = useDispatch()
  useEffect(() => {
    dispatch({
      type: 'SET_OPENED_POST',
      payload: {
        post,
      },
    })
  }, [post])

  return (
    <div className="flex mx-auto page">
      <Content bgColor="white">
        <FullWidthAdBanner />
        {emptySearchResults && <EmptySearchResults />}

        {!emptySearchResults && (
          <div className="flex bg-white mt-5">
            {searchResultsPagination?.data &&
            searchResultsPagination.data.length ? (
              <SearchResults />
            ) : (
              <div className="w-full">
                <div className="w-full">
                  <Post post={post} />
                  {relatedPostsPerTags ? (
                    <RelatedPostsHorizontalScroller
                      sectionName="Povezani članci"
                      posts={relatedPostsPerTags}
                    />
                  ) : null}
                  {newestFromCategory ? (
                    <RelatedPostsHorizontalScroller
                      sectionName="Najnovije iz rubrike"
                      posts={newestFromCategory}
                    />
                  ) : null}
                  <Comments post={post} />
                  {screenWidth < 900 ? (
                    <SideAdBanner position={BannerPosition.THREE} />
                  ) : null}
                  {screenWidth < 900 ? (
                    <SideAdBanner position={BannerPosition.FOUR} />
                  ) : null}
                </div>
              </div>
            )}
            <div className="hidden lg:block">
              <RightSidePanel />
            </div>
          </div>
        )}
      </Content>
      <SideVerticalAdvertisementBar />
    </div>
  )
}

export async function getServerSideProps(context) {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=180, stale-while-revalidate=200'
  )
  try {
    const reduxStore = initializeStore()
    const isPreviewMode = !!(
      context.query.preview !== undefined && context.query.preview
    )
    const newsResponse = await NewsApi.getBySlug(
      context.query.slug,
      context.query.preview
    )

    let commentsResponse = null
    if (!isPreviewMode) {
      commentsResponse = await CommentsApi.getByPostSlug(
        context.query.slug,
        1,
        20,
        getCookies(context, 'access_token')
      )
    }

    const categoriesResponse = await CategoryApi.getCategories()
    const categories = categoriesResponse.data
    const selectedCategory = categories.find(
      (category) =>
        category.name.toLowerCase().replace(/-/g, ' ') ===
        context.params.category.toLowerCase().replace(/-/g, ' ')
    )

    const selectedSubCategory =
      categories
        .map((c) => c.subcategories)
        .flat()
        .find(
          (subCategory) =>
            subCategory.name.toLowerCase() ===
            context.params.subCategory.toLowerCase().replace(/-/g, ' ')
        ) ?? null

    let relatedPostsPerTags = []
    if (newsResponse.data.tags) {
      try {
        const relatedPostsPerTagsResponse = await NewsApi.searchByTags(
          newsResponse.data.tags,
          1,
          10,
          [newsResponse.data.id]
        )
        if (relatedPostsPerTagsResponse.data.data) {
          relatedPostsPerTags = relatedPostsPerTagsResponse.data.data
        }
      } catch (e) {
        relatedPostsPerTags = []
        console.error(e)
        Sentry.captureException(e)
      }
    }

    let newestFromCategory = []
    if (selectedCategory) {
      try {
        const newestFromCategoryResponse = await NewsApi.searchByCategoryIds(
          [selectedCategory.id],
          1,
          10,
          [...relatedPostsPerTags.map((el) => el.id), newsResponse.data.id]
        )
        if (newestFromCategoryResponse.data.data) {
          newestFromCategory = newestFromCategoryResponse.data.data
        }
      } catch (e) {
        newestFromCategory = []
        console.error(e)
        Sentry.captureException(e)
      }
    }

    let banners = []
    if (selectedCategory) {
      const bannersResponse = await BannerApi.getBannersByCategoryId(
        selectedCategory.id
      )
      banners = bannersResponse.data
    }

    const authResponse = await authenticationApi.getUser(
      getCookies(context, 'access_token')
    )

    return {
      props: {
        currentCategory: selectedCategory,
        initialReduxState: {
          ...reduxStore.getState(),
          commentsPagination: {
            comments: commentsResponse?.data.data ?? [],
            total: commentsResponse?.data.total ?? 0,
            page: commentsResponse?.data.current_page ?? 0,
          },
          selectedCategory,
          selectedSubCategory,
          post: newsResponse.data,
          relatedPostsPerTags,
          newestFromCategory,
          showSubNavigationBarForCategory: selectedCategory.name,
          categories,
          banners,
          isUserLoggedIn: !!authResponse.data.id,
          user: authResponse.data.id ? authResponse.data : null,
        },
      },
    }
  } catch (e) {
    console.error(e)
    Sentry.captureException(e)
  }
}

const mapStateToProps = (state) => ({
  commentsPagination: state.commentsPagination,
  post: state.post,
  relatedPostsPerTags: state.relatedPostsPerTags,
  newestFromCategory: state.newestFromCategory,
  searchResultsPagination: state.searchResultsPagination,
  emptySearchResults: state.emptySearchResults,
})

export default connect(mapStateToProps)(PostPage)
